/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * «Jamaat-tider» on Bønnetider (2026-10-15): the words and the form ↔ body mapping, apart from the view so a test can
 * call them (panel.test.mjs). The rules are core's (core.ts Jamaat); the server checks again and has the last word.
 */
import { Jamaat } from '../core';

/** The form's fields, in the order a timetable reads, with the API's names. */
export const FIELDS: { key: string; api: string; label: string; fixedOnly?: boolean }[] = [
  { key: 'fajr', api: 'fajr', label: 'Fajr' },
  { key: 'dhuhr', api: 'dhuhr', label: 'Dhuhr' },
  { key: 'asr', api: 'asr', label: 'Asr' },
  { key: 'maghrib', api: 'maghrib', label: 'Maghrib' },
  { key: 'isha', api: 'isha', label: 'Isha' },
  { key: 'jumua_khutbah', api: 'jumuaKhutbah', label: 'Khutbah', fixedOnly: true },
  { key: 'jumua_jamaat', api: 'jumuaJamaat', label: "Jumu'ah", fixedOnly: true },
];

export interface JamaatForm { id: string | null; from: string; to: string; label: string; values: Record<string, string> }
export const blankForm = (): JamaatForm => ({ id: null, from: '', to: '', label: '', values: {} });

/** A period from the API → the form. `copy`: a new own period with an IRN period's days and times (to correct it). */
export function formFromPeriod(p: Record<string, any>, copy = false): JamaatForm {
  const values: Record<string, string> = {};
  for (const f of FIELDS) values[f.key] = p[f.api] || '';
  return { id: copy ? null : p.id, from: p.startsOn || '', to: p.endsOn || '', label: copy ? '' : p.label || '', values };
}

/** The form → the request body (camelCase, as the API reads it). Empty is null. */
export function bodyFromForm(f: JamaatForm): Record<string, string | null> {
  const out: Record<string, string | null> = { startsOn: f.from, endsOn: f.to, label: f.label.trim() || null };
  for (const x of FIELDS) out[x.api] = (f.values[x.key] || '').trim() || null;
  return out;
}

/** Core's check, before anything is sent: { ok, errors by field, warnings }. */
export function check(f: JamaatForm) {
  const v = Jamaat.validatePeriod({ starts_on: f.from, ends_on: f.to, label: f.label, ...f.values });
  const byField: Record<string, string> = {};
  for (const e of v.errors) if (!byField[e.field]) byField[e.field] = e.message;
  return { ok: v.ok, errors: v.errors, byField, warnings: v.warnings };
}

/** One period's times in words: «Fajr 07:15 · Maghrib 5 min etter · Jumu'ah 13:30». */
export function summary(p: Record<string, any>): string {
  return FIELDS.filter((f) => p[f.api]).map((f) => `${f.label} ${Jamaat.describe(p[f.api])}`).join(' · ') || '—';
}

/** Where a day's jamaat came from, as the preview says it. */
export const SOURCE_NO: Record<string, string> = { override: 'overstyrt', staff: 'egen', irn: 'IRN', timetable: 'årstabell' };
export const sourceWord = (s: string | null | undefined): string => (s ? SOURCE_NO[s] || s : '');

/**
 * One cell of the 14-day preview: begins, and the jamaat with where it came from. On a Friday the Dhuhr cell is
 * Jumu'ah (as the app and the screens show it). A closed day has no jamaat.
 */
export function previewCell(day: Record<string, any>, prayer: string): { begins: string; jamaat: string; source: string } {
  const begins = (day.begins && day.begins[prayer]) || '—';
  if (day.isClosed) return { begins, jamaat: 'stengt', source: '' };
  const friday = prayer === 'dhuhr' && day.isFriday && day.jamaat && day.jamaat.jumuaJamaat;
  const value = friday ? day.jamaat.jumuaJamaat : day.jamaat && day.jamaat[prayer];
  const src = day.sources ? day.sources[friday ? 'jumua_jamaat' : prayer] : null;
  return { begins, jamaat: value ? (friday ? `Jumu'ah ${value}` : value) : '—', source: value ? sourceWord(src) : '' };
}
